import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  IconButton,
  Badge,
  VStack,
  HStack,
  Button,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { useRecoilValue } from "recoil";
import { customColors } from "../../../atoms/customColors";
import moment from "moment";
import "moment/locale/pt-br";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaArrowLeft,
  FaDownload,
} from "react-icons/fa";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ListaBoletoAPI from "../../../services/ListaBoleto";
import BolepixGestaoAPI, {
  LinhaGestao,
  SITUACAO,
  Situacao,
} from "../../../services/BolepixGestao";
import jwt_decode from "jwt-decode";

/**
 * Um dia do calendário, como a core devolve em /bolepix/calendar.
 *
 * As três palavras não são sinônimos e a tela nunca as troca:
 *
 *  - PREVISTO  — o que VENCE no dia/mês. Régua de competência.
 *  - EFETIVADO — dos boletos que vencem no dia/mês, os que foram pagos. O
 *                dinheiro pode ter entrado em outro mês.
 *  - RECEBIDO  — o que ENTROU no caixa no dia/mês, pela data do lançamento no
 *                extrato, venha o boleto do vencimento que vier.
 *
 * Agosto pode receber dinheiro de boleto que vencia em julho (atrasado) ou em
 * setembro (adiantado): isso é "recebido em agosto", nunca "efetivado de
 * agosto".
 */
interface CalendarItem {
  /** Vencimento ou data de crédito no extrato, conforme o eixo escolhido. */
  data: string;
  /** Nome antigo do mesmo campo — a core só devolve `data` a partir do deploy
   *  desta versão. Mantido para a tela não ficar vazia contra a core antiga. */
  dataVencimento?: string;

  // competência
  qtdePrevista: number;
  valorPrevisto: number;
  qtdeEfetivada: number;
  valorEfetivado: number;
  qtdeVencida: number;
  valorVencido: number;
  qtdeAVencer: number;
  valorAVencer: number;
  qtdeNaoEmitida: number;

  // caixa
  qtdeRecebida: number;
  valorRecebido: number;
  qtdeRecebidaDoMes: number;
  valorRecebidoDoMes: number;
  qtdeRecebidaDeVencimentoAnterior: number;
  valorRecebidoDeVencimentoAnterior: number;
  qtdeRecebidaDeVencimentoFuturo: number;
  valorRecebidoDeVencimentoFuturo: number;

  /** O que caiu na conta (extrato tipo 26) — já com a taxa descontada. */
  valorLiquido: number;
  /** Creditou e foi devolvido depois (extrato tipo 27). Fora do recebido. */
  qtdeEstornada: number;
  valorEstornada: number;
  /** Dias entre vencimento e crédito. Negativo = pago adiantado. */
  diasAteRecebimentoMedio: number | null;
}

type Eixo = "vencimento" | "recebimento";

const ZERO: Omit<CalendarItem, "data" | "diasAteRecebimentoMedio"> = {
  qtdePrevista: 0,
  valorPrevisto: 0,
  qtdeEfetivada: 0,
  valorEfetivado: 0,
  qtdeVencida: 0,
  valorVencido: 0,
  qtdeAVencer: 0,
  valorAVencer: 0,
  qtdeNaoEmitida: 0,
  qtdeRecebida: 0,
  valorRecebido: 0,
  qtdeRecebidaDoMes: 0,
  valorRecebidoDoMes: 0,
  qtdeRecebidaDeVencimentoAnterior: 0,
  valorRecebidoDeVencimentoAnterior: 0,
  qtdeRecebidaDeVencimentoFuturo: 0,
  valorRecebidoDeVencimentoFuturo: 0,
  valorLiquido: 0,
  qtdeEstornada: 0,
  valorEstornada: 0,
};

interface JWTToken {
  idCliente: number;
}

const fmtBRL = (n: number) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtDias = (n: number) => {
  const v = Number(n.toFixed(1).replace(/\.0$/, ""));
  if (v === 0) return "no dia do vencimento";
  return v > 0 ? `D+${v} do vencimento` : `${Math.abs(v)} dia(s) adiantado`;
};

export default function CalendarioBoletos() {
  const colors = useRecoilValue(customColors);
  const [currentDate, setCurrentDate] = useState(moment());
  const [calendarData, setCalendarData] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDay, setSelectedDay] = useState<moment.Moment | null>(null);
  const [boletosDia, setBoletosDia] = useState<LinhaGestao[]>([]);
  const [carregandoDia, setCarregandoDia] = useState(false);
  const [exportando, setExportando] = useState(false);
  // Competência x caixa. O mesmo boleto vive em dias diferentes nos dois eixos —
  // vence no 31, entra no caixa no 4 — então a escolha é sempre explícita e as
  // medidas de um eixo nunca aparecem com o nome do outro.
  const [eixo, setEixo] = useState<Eixo>("vencimento");
  const porRecebimento = eixo === "recebimento";
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const jwtToken: JWTToken = token ? jwt_decode(token) : { idCliente: 0 };
  const idCliente = jwtToken.idCliente;

  const startOfMonth = currentDate.clone().startOf("month");
  const endOfMonth = currentDate.clone().endOf("month");
  const startDate = startOfMonth.clone().startOf("week");
  const endDate = endOfMonth.clone().endOf("week");
  const mesLabel = currentDate.format("MMMM [de] YYYY");

  const fetchCalendarData = async () => {
    if (!idCliente) return;
    setLoading(true);
    try {
      const response = await ListaBoletoAPI.getCalendarData(
        startOfMonth.format("YYYY-MM-DD"),
        endOfMonth.format("YYYY-MM-DD"),
        idCliente,
        undefined,
        eixo
      );
      // A core antiga não devolve os campos novos; normalizar aqui evita NaN
      // espalhado pela tela enquanto o deploy não acontece.
      const dias: CalendarItem[] = (
        Array.isArray(response.data) ? response.data : []
      ).map((d: any) => ({
        ...ZERO,
        ...d,
        data: d.data || d.dataVencimento || "",
        diasAteRecebimentoMedio:
          d.diasAteRecebimentoMedio == null
            ? null
            : Number(d.diasAteRecebimentoMedio),
      }));
      setCalendarData(dias);
    } catch (error) {
      console.error("Erro ao carregar calendário:", error);
      setCalendarData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, idCliente, eixo]);

  const getDayData = (date: moment.Moment): CalendarItem | undefined => {
    const dateStr = date.format("YYYY-MM-DD");
    return calendarData.find((item) => (item.data || "").startsWith(dateStr));
  };

  const totalMes = calendarData.reduce(
    (acc, item) => ({
      qtdePrevista: acc.qtdePrevista + item.qtdePrevista,
      valorPrevisto: acc.valorPrevisto + item.valorPrevisto,
      qtdeEfetivada: acc.qtdeEfetivada + item.qtdeEfetivada,
      valorEfetivado: acc.valorEfetivado + item.valorEfetivado,
      qtdeVencida: acc.qtdeVencida + item.qtdeVencida,
      valorVencido: acc.valorVencido + item.valorVencido,
      qtdeAVencer: acc.qtdeAVencer + item.qtdeAVencer,
      valorAVencer: acc.valorAVencer + item.valorAVencer,
      qtdeNaoEmitida: acc.qtdeNaoEmitida + item.qtdeNaoEmitida,
      qtdeRecebida: acc.qtdeRecebida + item.qtdeRecebida,
      valorRecebido: acc.valorRecebido + item.valorRecebido,
      qtdeRecebidaDoMes: acc.qtdeRecebidaDoMes + item.qtdeRecebidaDoMes,
      valorRecebidoDoMes: acc.valorRecebidoDoMes + item.valorRecebidoDoMes,
      qtdeRecebidaDeVencimentoAnterior:
        acc.qtdeRecebidaDeVencimentoAnterior +
        item.qtdeRecebidaDeVencimentoAnterior,
      valorRecebidoDeVencimentoAnterior:
        acc.valorRecebidoDeVencimentoAnterior +
        item.valorRecebidoDeVencimentoAnterior,
      qtdeRecebidaDeVencimentoFuturo:
        acc.qtdeRecebidaDeVencimentoFuturo +
        item.qtdeRecebidaDeVencimentoFuturo,
      valorRecebidoDeVencimentoFuturo:
        acc.valorRecebidoDeVencimentoFuturo +
        item.valorRecebidoDeVencimentoFuturo,
      valorLiquido: acc.valorLiquido + item.valorLiquido,
      qtdeEstornada: acc.qtdeEstornada + (item.qtdeEstornada || 0),
      valorEstornada: acc.valorEstornada + (item.valorEstornada || 0),
    }),
    { ...ZERO }
  );

  // Média ponderada pela quantidade de cada dia — a média das médias diárias
  // daria o mesmo peso a um dia com 1 boleto e a um com 20.
  const diasAteRecebimento = (() => {
    const base: keyof CalendarItem = porRecebimento
      ? "qtdeRecebida"
      : "qtdeEfetivada";
    const comDado = calendarData.filter(
      (d) => d.diasAteRecebimentoMedio != null && (d[base] as number) > 0
    );
    const total = comDado.reduce((a, d) => a + (d[base] as number), 0);
    if (!total) return null;
    return (
      comDado.reduce(
        (a, d) => a + (d.diasAteRecebimentoMedio || 0) * (d[base] as number),
        0
      ) / total
    );
  })();

  const pctEfetivado =
    totalMes.valorPrevisto > 0
      ? Math.round((totalMes.valorEfetivado / totalMes.valorPrevisto) * 100)
      : 0;

  const prevMonth = () =>
    setCurrentDate(currentDate.clone().subtract(1, "month"));
  const nextMonth = () => setCurrentDate(currentDate.clone().add(1, "month"));
  const irParaHoje = () => setCurrentDate(moment());

  const days: moment.Moment[] = [];
  let day = startDate.clone();
  while (day.isBefore(endDate) || day.isSame(endDate, "day")) {
    days.push(day.clone());
    day.add(1, "day");
  }

  const openModal = (dia: moment.Moment) => {
    setSelectedDay(dia);
    onOpen();
  };

  /**
   * Leva o número clicado para a LISTA daquele número, já filtrada.
   * Antes daqui todo botão caía em /vendas/bolepix sem filtro nenhum: o cliente
   * via "12 vencidos · R$ 4.300" e tinha que refazer a busca na mão — e com
   * outra régua de data, que dava outro total.
   */
  const verBoletosDoDia = (dia: moment.Moment | null) => {
    const data = (dia ?? moment()).format("YYYY-MM-DD");
    const situacao = porRecebimento
      ? "pagos"
      : (dia ?? moment()).isBefore(moment(), "day")
        ? "vencidos"
        : "avencer";

    navigate(`/vendas/bolepix/gestao?situacao=${situacao}&de=${data}&ate=${data}`);
  };

  const selectedDayData = selectedDay ? getDayData(selectedDay) : null;

  // Situação daquele dia na régua do eixo escolhido — a mesma que a lista e o
  // painel de gestão usam (core: Service/BolepixGestaoSql.cs).
  const situacaoDoDia = (dia: moment.Moment | null): Situacao =>
    porRecebimento
      ? SITUACAO.PAGO
      : (dia ?? moment()).isBefore(moment(), "day")
        ? SITUACAO.VENCIDO
        : SITUACAO.A_VENCER;

  // O modal mostrava só agregados: o cliente via "12 boletos" e não tinha como
  // saber QUAIS. Aqui vem a lista do dia, no mesmo critério do número.
  useEffect(() => {
    if (!isOpen || !selectedDay) return;
    const data = selectedDay.format("YYYY-MM-DD");

    setCarregandoDia(true);
    BolepixGestaoAPI.getLista(
      situacaoDoDia(selectedDay),
      { de: data, ate: data },
      1,
      50
    )
      .then((r) => setBoletosDia(r.registros || []))
      .catch(() => setBoletosDia([]))
      .finally(() => setCarregandoDia(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDay, porRecebimento]);

  async function exportarMes() {
    setExportando(true);
    try {
      await BolepixGestaoAPI.exportar(
        porRecebimento ? SITUACAO.PAGO : SITUACAO.A_VENCER,
        {
          de: startOfMonth.format("YYYY-MM-DD"),
          ate: endOfMonth.format("YYYY-MM-DD"),
        }
      );
    } finally {
      setExportando(false);
    }
  }

  const temMovimento = (d?: CalendarItem) =>
    !!d && (porRecebimento ? d.qtdeRecebida > 0 : d.qtdePrevista > 0);

  // A cor do dia responde "preciso olhar para este dia?".
  const corDoDia = (d?: CalendarItem) => {
    if (!d) return null;
    if (porRecebimento) return d.qtdeRecebida > 0 ? colors.GREENMONEDD : null;
    if (d.qtdePrevista === 0) return null;
    if (d.qtdeVencida > 0) return colors.REDMONEDD;
    if (d.qtdeEfetivada === d.qtdePrevista) return colors.GREENMONEDD;
    return colors.BLUEMONEDDBUTTONS;
  };

  const CardResumo = ({
    titulo,
    subtitulo,
    qtde,
    valor,
    cor,
    detalhe,
  }: {
    titulo: string;
    subtitulo: string;
    qtde: number;
    valor: number;
    cor: string;
    detalhe?: string;
  }) => (
    <Box
      bg={colors.WHITE}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.200"
      borderLeftWidth="4px"
      borderLeftColor={cor}
      px={4}
      py={3}
      flex="1"
      minW="200px"
    >
      <Text fontSize="xs" color={colors.GRAY_DARK} fontWeight="bold">
        {titulo}
      </Text>
      <Text fontSize="2xs" color={colors.GRAY_DARKER} mb={1}>
        {subtitulo}
      </Text>
      <Text fontSize="xl" fontWeight="bold" color={cor} lineHeight="1.2">
        {fmtBRL(valor)}
      </Text>
      <Text fontSize="xs" color={colors.GRAY_DARK}>
        {qtde} {qtde === 1 ? "boleto" : "boletos"}
        {detalhe ? ` · ${detalhe}` : ""}
      </Text>
    </Box>
  );

  const LinhaMedida = ({
    rotulo,
    qtde,
    valor,
    cor,
  }: {
    rotulo: string;
    qtde: number;
    valor: number;
    cor: string;
  }) => (
    <HStack fontSize="2xs" spacing={1} align="baseline">
      <Text color={colors.GRAY_DARK} flexShrink={0}>
        {rotulo}
      </Text>
      <Text color={cor} fontWeight="bold" noOfLines={1}>
        {fmtBRL(valor)}
      </Text>
      <Text color={colors.GRAY_DARK}>({qtde})</Text>
    </HStack>
  );

  const LinhaDetalhe = ({
    rotulo,
    valor,
    qtde,
    cor,
    negrito,
  }: {
    rotulo: string;
    valor: number;
    qtde?: number;
    cor?: string;
    negrito?: boolean;
  }) => (
    <Flex justify="space-between" align="baseline" gap={2}>
      <Text
        color={negrito ? colors.BLUEMONEDDBACKGROUND : colors.GRAY_DARK}
        fontWeight={negrito ? "bold" : "normal"}
        fontSize="sm"
      >
        {rotulo}
      </Text>
      <Text
        fontWeight="bold"
        color={cor || colors.BLUEMONEDDBACKGROUND}
        fontSize="sm"
        whiteSpace="nowrap"
      >
        {fmtBRL(valor)}
        {qtde != null ? ` · ${qtde}` : ""}
      </Text>
    </Flex>
  );

  return (
    <>
      <Flex
        flexDir="column"
        height="90vh"
        width="100%"
        bgColor={colors.GRAY_LIGHTEST}
        p={{ base: 3, md: 6 }}
        overflow="hidden"
      >
        <Box
          bg={colors.WHITE}
          borderRadius="lg"
          boxShadow="md"
          p={{ base: 3, md: 5 }}
          flex="1"
          overflow="hidden"
          display="flex"
          flexDir="column"
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            gap={3}
            flexWrap="wrap"
          >
            <Flex alignItems="center" gap={2}>
              <IconButton
                aria-label="Mês anterior"
                icon={<FaChevronLeft />}
                onClick={prevMonth}
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                size="sm"
                _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                borderRadius="full"
              />
              <Text
                fontSize="xl"
                fontWeight="bold"
                color={colors.BLUEMONEDDBACKGROUND}
                minW="200px"
                textAlign="center"
              >
                {mesLabel}
              </Text>
              <IconButton
                aria-label="Próximo mês"
                icon={<FaChevronRight />}
                onClick={nextMonth}
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                size="sm"
                _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                borderRadius="full"
              />
              <Button
                size="xs"
                variant="outline"
                onClick={irParaHoje}
                color={colors.BLUEMONEDDBUTTONS}
                borderColor={colors.BLUEMONEDDBUTTONS}
                ml={1}
              >
                Hoje
              </Button>
            </Flex>

            <HStack
              spacing={0}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
            >
              {(
                [
                  ["vencimento", "Por vencimento"],
                  ["recebimento", "Por recebimento"],
                ] as [Eixo, string][]
              ).map(([valor, rotulo]) => (
                <Button
                  key={valor}
                  size="xs"
                  borderRadius="0"
                  px={3}
                  onClick={() => setEixo(valor)}
                  bgColor={
                    eixo === valor ? colors.BLUEMONEDDBUTTONS : "transparent"
                  }
                  color={eixo === valor ? colors.WHITE : colors.GRAY_DARK}
                  _hover={{
                    bgColor:
                      eixo === valor
                        ? colors.BLUEMONEDDRGB2
                        : colors.GRAY_LIGHTEST,
                  }}
                >
                  {rotulo}
                </Button>
              ))}
            </HStack>

            <Button
              bgColor="transparent"
              color={colors.BLUEMONEDDBUTTONS}
              size="sm"
              leftIcon={<FaDownload />}
              isLoading={exportando}
              onClick={exportarMes}
              fontWeight="medium"
              mr={2}
            >
              Exportar mês
            </Button>

            <Button
              bgColor="transparent"
              color={colors.BLUEMONEDDBUTTONS}
              size="sm"
              onClick={() => navigate("/vendas/bolepix/gestao")}
              fontWeight="medium"
              mr={2}
            >
              Painel de gestão
            </Button>

            <Button
              bgColor="transparent"
              color={colors.BLUEMONEDDBUTTONS}
              size="sm"
              onClick={() => navigate("/vendas/bolepix")}
              leftIcon={<FaArrowLeft />}
              _hover={{ color: colors.BLUEMONEDDRGB2 }}
              fontWeight="medium"
            >
              Voltar
            </Button>
          </Flex>

          {/* A frase que separa as duas leituras — é exatamente o que o cliente
              cobra: o que é competência e o que é caixa. */}
          <Text fontSize="xs" color={colors.GRAY_DARK} mb={3}>
            {porRecebimento ? (
              <>
                <b>Recebido</b> é o dinheiro que entrou em {mesLabel}, pela data
                do lançamento no extrato — não importa de que mês era o
                vencimento do boleto. Inclui pagamentos adiantados e em atraso.
              </>
            ) : (
              <>
                <b>Previsto</b> e <b>efetivado</b> olham a data de{" "}
                <b>vencimento</b>: são os boletos que vencem em {mesLabel}. Um
                boleto efetivado pode ter tido o dinheiro creditado em outro mês
                — para ver o caixa, use <b>Por recebimento</b>.
              </>
            )}
          </Text>

          <Flex gap={3} mb={4} flexWrap="wrap">
            {porRecebimento ? (
              <>
                <CardResumo
                  titulo="Recebido no mês"
                  subtitulo="pela data do extrato · valor dos boletos"
                  qtde={totalMes.qtdeRecebida}
                  valor={totalMes.valorRecebido}
                  cor={colors.GREENMONEDD}
                  detalhe={
                    diasAteRecebimento != null
                      ? `crédito em média ${fmtDias(diasAteRecebimento)}`
                      : undefined
                  }
                />
                <CardResumo
                  titulo="Creditado na conta"
                  subtitulo="líquido, já sem as taxas"
                  qtde={totalMes.qtdeRecebida}
                  valor={totalMes.valorLiquido}
                  cor={colors.BLUEMONEDDBACKGROUND}
                  detalhe={
                    totalMes.valorRecebido > 0
                      ? `taxas ${fmtBRL(
                        totalMes.valorRecebido - totalMes.valorLiquido
                      )}`
                      : undefined
                  }
                />
                <Box
                  bg={colors.WHITE}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderLeftWidth="4px"
                  borderLeftColor={colors.YELLOWMONEDD}
                  px={4}
                  py={3}
                  flex="1.4"
                  minW="280px"
                >
                  <Text
                    fontSize="xs"
                    color={colors.GRAY_DARK}
                    fontWeight="bold"
                  >
                    De onde veio o dinheiro
                  </Text>
                  <Text fontSize="2xs" color={colors.GRAY_DARKER} mb={1}>
                    vencimento dos boletos recebidos em {mesLabel}
                  </Text>
                  <VStack align="stretch" spacing={0}>
                    <LinhaDetalhe
                      rotulo="Vencimento neste mês"
                      valor={totalMes.valorRecebidoDoMes}
                      qtde={totalMes.qtdeRecebidaDoMes}
                    />
                    <LinhaDetalhe
                      rotulo="De meses anteriores (atrasados)"
                      valor={totalMes.valorRecebidoDeVencimentoAnterior}
                      qtde={totalMes.qtdeRecebidaDeVencimentoAnterior}
                      cor={colors.REDMONEDD}
                    />
                    <LinhaDetalhe
                      rotulo="De meses futuros (adiantados)"
                      valor={totalMes.valorRecebidoDeVencimentoFuturo}
                      qtde={totalMes.qtdeRecebidaDeVencimentoFuturo}
                      cor={colors.YELLOWMONEDD}
                    />
                    {/* Estorno não é recebimento: creditou e voltou. Fica de
                        fora dos totais acima e visível aqui, senão o dia mudava
                        de valor sem explicação. */}
                    {totalMes.qtdeEstornada > 0 && (
                      <>
                        <Divider my={1} />
                        <LinhaDetalhe
                          rotulo="Estornado depois (não entra no recebido)"
                          valor={totalMes.valorEstornada}
                          qtde={totalMes.qtdeEstornada}
                          cor={colors.REDMONEDD}
                        />
                      </>
                    )}
                  </VStack>
                </Box>
              </>
            ) : (
              <>
                <CardResumo
                  titulo="Previsto no mês"
                  subtitulo={`vencem em ${mesLabel}`}
                  qtde={totalMes.qtdePrevista}
                  valor={totalMes.valorPrevisto}
                  cor={colors.BLUEMONEDDBACKGROUND}
                  detalhe={
                    totalMes.qtdeNaoEmitida > 0
                      ? `${totalMes.qtdeNaoEmitida} ainda não emitido(s)`
                      : undefined
                  }
                />
                <CardResumo
                  titulo="Efetivado do mês"
                  subtitulo="destes boletos, os que foram pagos"
                  qtde={totalMes.qtdeEfetivada}
                  valor={totalMes.valorEfetivado}
                  cor={colors.GREENMONEDD}
                  detalhe={
                    diasAteRecebimento != null
                      ? `${pctEfetivado}% do previsto · crédito ${fmtDias(
                        diasAteRecebimento
                      )}`
                      : `${pctEfetivado}% do previsto`
                  }
                />
                <CardResumo
                  titulo="Vencido em aberto"
                  subtitulo="venceu neste mês e não foi pago"
                  qtde={totalMes.qtdeVencida}
                  valor={totalMes.valorVencido}
                  cor={colors.REDMONEDD}
                />
                <CardResumo
                  titulo="A vencer"
                  subtitulo="ainda dentro do prazo"
                  qtde={totalMes.qtdeAVencer}
                  valor={totalMes.valorAVencer}
                  cor={colors.YELLOWMONEDD}
                />
              </>
            )}
          </Flex>

          {loading ? (
            <Flex flex="1" align="center" justify="center">
              <Spinner
                size="xl"
                color={colors.BLUEMONEDDBUTTONS}
                thickness="4px"
              />
            </Flex>
          ) : (
            <Box flex="1" overflow="auto">
              <Grid templateColumns="repeat(7, 1fr)" gap={{ base: 1, md: 2 }} mb={1}>
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((dia) => (
                  <GridItem key={dia}>
                    <Text
                      textAlign="center"
                      fontWeight="bold"
                      color={colors.BLUEMONEDDBACKGROUND}
                      fontSize="xs"
                      py={1}
                    >
                      {dia}
                    </Text>
                  </GridItem>
                ))}
              </Grid>

              {/* gap e altura menores em telas baixas/estreitas (4:3, ultrawide
                  1080p): com 104px fixos e gap 2 o mês não cabia e os badges de
                  cada dia estouravam a célula. */}
              <Grid templateColumns="repeat(7, 1fr)" gap={{ base: 1, md: 2 }}>
                {days.map((dia, idx) => {
                  const dayData = getDayData(dia);
                  const isCurrentMonth = dia.isSame(currentDate, "month");
                  const isToday = dia.isSame(moment(), "day");
                  const temDados = temMovimento(dayData);
                  const cor = corDoDia(dayData);

                  return (
                    <GridItem
                      key={idx}
                      minH={{ base: "68px", md: "104px" }}
                      bg={isCurrentMonth ? colors.WHITE : colors.GRAY_LIGHTEST}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={
                        isToday ? colors.BLUEMONEDDBUTTONS : "gray.200"
                      }
                      borderLeftWidth={cor ? "3px" : "1px"}
                      borderLeftColor={cor || "gray.200"}
                      p={{ base: 1, md: 2 }}
                      transition="all 0.15s"
                      _hover={{
                        boxShadow: temDados && isCurrentMonth ? "md" : "none",
                      }}
                      cursor={isCurrentMonth && temDados ? "pointer" : "default"}
                      opacity={isCurrentMonth ? 1 : 0.45}
                      onClick={() =>
                        isCurrentMonth && temDados && openModal(dia)
                      }
                    >
                      <VStack align="stretch" spacing={1} h="100%">
                        <Flex justify="space-between" align="center">
                          <Text
                            fontWeight={isToday ? "bold" : "medium"}
                            fontSize="sm"
                            color={
                              isToday
                                ? colors.BLUEMONEDDBUTTONS
                                : colors.GRAY_DARK
                            }
                          >
                            {dia.format("D")}
                          </Text>
                          {temDados && (
                            <Badge
                              bg={cor || colors.BLUEMONEDDBUTTONS}
                              color={colors.WHITE}
                              fontSize="2xs"
                              px={2}
                              borderRadius="full"
                            >
                              {porRecebimento
                                ? dayData!.qtdeRecebida
                                : dayData!.qtdePrevista}
                            </Badge>
                          )}
                        </Flex>

                        {temDados && porRecebimento && (
                          <VStack
                            align="stretch"
                            spacing={0.5}
                            flex="1"
                            display={{ base: "none", md: "flex" }}
                          >
                            <LinhaMedida
                              rotulo="Recebido"
                              qtde={dayData!.qtdeRecebida}
                              valor={dayData!.valorRecebido}
                              cor={colors.GREENMONEDD}
                            />
                            <Text fontSize="2xs" color={colors.GRAY_DARK}>
                              líquido {fmtBRL(dayData!.valorLiquido)}
                            </Text>
                            {dayData!.qtdeRecebidaDeVencimentoAnterior > 0 && (
                              <Text fontSize="2xs" color={colors.REDMONEDD}>
                                {dayData!.qtdeRecebidaDeVencimentoAnterior} de
                                mês anterior
                              </Text>
                            )}
                            {dayData!.qtdeRecebidaDeVencimentoFuturo > 0 && (
                              <Text fontSize="2xs" color={colors.YELLOWMONEDD}>
                                {dayData!.qtdeRecebidaDeVencimentoFuturo}{" "}
                                adiantado(s)
                              </Text>
                            )}
                          </VStack>
                        )}

                        {temDados && !porRecebimento && (
                          <VStack
                            align="stretch"
                            spacing={0.5}
                            flex="1"
                            display={{ base: "none", md: "flex" }}
                          >
                            <LinhaMedida
                              rotulo="Prev"
                              qtde={dayData!.qtdePrevista}
                              valor={dayData!.valorPrevisto}
                              cor={colors.BLUEMONEDDBACKGROUND}
                            />
                            {dayData!.qtdeEfetivada > 0 && (
                              <LinhaMedida
                                rotulo="Efet"
                                qtde={dayData!.qtdeEfetivada}
                                valor={dayData!.valorEfetivado}
                                cor={colors.GREENMONEDD}
                              />
                            )}
                            {dayData!.qtdeVencida > 0 && (
                              <LinhaMedida
                                rotulo="Venc"
                                qtde={dayData!.qtdeVencida}
                                valor={dayData!.valorVencido}
                                cor={colors.REDMONEDD}
                              />
                            )}
                            {dayData!.qtdeAVencer > 0 && (
                              <LinhaMedida
                                rotulo="Aberto"
                                qtde={dayData!.qtdeAVencer}
                                valor={dayData!.valorAVencer}
                                cor={colors.YELLOWMONEDD}
                              />
                            )}
                          </VStack>
                        )}
                      </VStack>
                    </GridItem>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Flex justify="center" mt={3} gap={5} flexWrap="wrap">
            {porRecebimento ? (
              <Text fontSize="xs" color={colors.GRAY_DARK} textAlign="center">
                Cada dia mostra o que <b>entrou</b> nele, pela data do lançamento
                no extrato. Em destaque o valor dos boletos; abaixo, o líquido
                creditado.
              </Text>
            ) : (
              <>
                <HStack>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={colors.GREENMONEDD}
                  />
                  <Text fontSize="xs" color={colors.GRAY_DARK}>
                    Tudo efetivado
                  </Text>
                </HStack>
                <HStack>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={colors.REDMONEDD}
                  />
                  <Text fontSize="xs" color={colors.GRAY_DARK}>
                    Tem boleto vencido em aberto
                  </Text>
                </HStack>
                <HStack>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={colors.BLUEMONEDDBUTTONS}
                  />
                  <Text fontSize="xs" color={colors.GRAY_DARK}>
                    Ainda no prazo
                  </Text>
                </HStack>
              </>
            )}
          </Flex>
        </Box>
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "full", md: "lg" }}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={colors.WHITE} borderRadius="lg" boxShadow="xl">
          <ModalHeader
            bg={colors.BLUEMONEDDBACKGROUND}
            color={colors.WHITE}
            borderTopRadius="lg"
            py={3}
            textAlign="center"
          >
            <Flex align="center" justify="center" gap={1} flexDir="column">
              <Flex align="center" gap={2}>
                <Icon as={FaEye} />
                <Text fontWeight="bold" fontSize="md">
                  {selectedDay?.format("DD [de] MMMM [de] YYYY")}
                </Text>
              </Flex>
              <Text fontSize="2xs" fontWeight="normal" opacity={0.9}>
                {porRecebimento
                  ? "o que entrou neste dia (data do extrato)"
                  : "o que vence neste dia"}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color={colors.WHITE} />
          <ModalBody p={5}>
            {selectedDayData && porRecebimento ? (
              <VStack align="stretch" spacing={2}>
                <LinhaDetalhe
                  rotulo="Recebido no dia"
                  valor={selectedDayData.valorRecebido}
                  qtde={selectedDayData.qtdeRecebida}
                  cor={colors.GREENMONEDD}
                  negrito
                />
                <LinhaDetalhe
                  rotulo="Creditado (líquido)"
                  valor={selectedDayData.valorLiquido}
                />
                <LinhaDetalhe
                  rotulo="Taxas"
                  valor={
                    selectedDayData.valorRecebido - selectedDayData.valorLiquido
                  }
                  cor={colors.GRAY_DARK}
                />
                <Divider />
                <Text fontSize="xs" color={colors.GRAY_DARK} fontWeight="bold">
                  De onde veio
                </Text>
                <LinhaDetalhe
                  rotulo="Vencimento neste mês"
                  valor={selectedDayData.valorRecebidoDoMes}
                  qtde={selectedDayData.qtdeRecebidaDoMes}
                />
                <LinhaDetalhe
                  rotulo="De meses anteriores"
                  valor={selectedDayData.valorRecebidoDeVencimentoAnterior}
                  qtde={selectedDayData.qtdeRecebidaDeVencimentoAnterior}
                  cor={colors.REDMONEDD}
                />
                <LinhaDetalhe
                  rotulo="De meses futuros (adiantado)"
                  valor={selectedDayData.valorRecebidoDeVencimentoFuturo}
                  qtde={selectedDayData.qtdeRecebidaDeVencimentoFuturo}
                  cor={colors.YELLOWMONEDD}
                />
                {selectedDayData.diasAteRecebimentoMedio != null && (
                  <Text fontSize="xs" color={colors.GRAY_DARK}>
                    Crédito em média{" "}
                    <b>{fmtDias(selectedDayData.diasAteRecebimentoMedio)}</b>.
                  </Text>
                )}
                <Button
                  size="sm"
                  mt={1}
                  bgColor={colors.BLUEMONEDDBUTTONS}
                  color={colors.WHITE}
                  onClick={() => verBoletosDoDia(selectedDay)}
                  _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                >
                  Ver boletos
                </Button>
              </VStack>
            ) : selectedDayData ? (
              <VStack align="stretch" spacing={2}>
                <LinhaDetalhe
                  rotulo="Previsto (vence no dia)"
                  valor={selectedDayData.valorPrevisto}
                  qtde={selectedDayData.qtdePrevista}
                  negrito
                />
                <Divider />
                <LinhaDetalhe
                  rotulo="Efetivado (foi pago)"
                  valor={selectedDayData.valorEfetivado}
                  qtde={selectedDayData.qtdeEfetivada}
                  cor={colors.GREENMONEDD}
                />
                <LinhaDetalhe
                  rotulo="Vencido em aberto"
                  valor={selectedDayData.valorVencido}
                  qtde={selectedDayData.qtdeVencida}
                  cor={colors.REDMONEDD}
                />
                <LinhaDetalhe
                  rotulo="A vencer"
                  valor={selectedDayData.valorAVencer}
                  qtde={selectedDayData.qtdeAVencer}
                  cor={colors.YELLOWMONEDD}
                />
                {selectedDayData.qtdeEfetivada > 0 && (
                  <>
                    <Divider />
                    <Text fontSize="xs" color={colors.GRAY_DARK}>
                      Do que foi pago entraram{" "}
                      <b>{fmtBRL(selectedDayData.valorLiquido)}</b> líquidos
                      {selectedDayData.diasAteRecebimentoMedio != null ? (
                        <>
                          , em média{" "}
                          <b>
                            {fmtDias(selectedDayData.diasAteRecebimentoMedio)}
                          </b>
                        </>
                      ) : null}
                      . O dia do crédito aparece em <b>Por recebimento</b>.
                    </Text>
                  </>
                )}
                {selectedDayData.qtdeNaoEmitida > 0 && (
                  <Text fontSize="xs" color="orange.600">
                    {selectedDayData.qtdeNaoEmitida}{" "}
                    {selectedDayData.qtdeNaoEmitida === 1
                      ? "parcela ainda não foi emitida"
                      : "parcelas ainda não foram emitidas"}{" "}
                    — o pagador ainda não tem boleto para pagar.
                  </Text>
                )}
                <Button
                  size="sm"
                  mt={1}
                  bgColor={colors.BLUEMONEDDBUTTONS}
                  color={colors.WHITE}
                  onClick={() => verBoletosDoDia(selectedDay)}
                  _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                >
                  Ver boletos
                </Button>
              </VStack>
            ) : (
              <Text textAlign="center" color={colors.GRAY_DARK}>
                {porRecebimento
                  ? "Nada entrou neste dia."
                  : "Nenhum boleto vence neste dia."}
              </Text>
            )}

            {/* QUAIS boletos são esses. O rótulo diz o recorte, porque no eixo
                vencimento a lista traz o que está EM ABERTO — os já pagos
                daquele vencimento estão no "efetivado" acima, não aqui. */}
            <Divider my={4} />
            <Text fontSize="xs" fontWeight="bold" color={colors.GRAY_DARK} mb={2}>
              {porRecebimento
                ? "RECEBIDOS NESTE DIA"
                : selectedDay && selectedDay.isBefore(moment(), "day")
                  ? "EM ABERTO, VENCIDOS NESTE DIA"
                  : "A VENCER NESTE DIA"}
            </Text>

            {carregandoDia ? (
              <Flex justify="center" py={4}>
                <Spinner size="sm" color={colors.BLUEMONEDDBUTTONS} />
              </Flex>
            ) : boletosDia.length === 0 ? (
              <Text fontSize="xs" color={colors.GRAY_DARK}>
                Nenhum boleto neste recorte.
              </Text>
            ) : (
              <VStack align="stretch" spacing={1} maxH="240px" overflowY="auto">
                {boletosDia.map((b) => (
                  <Flex
                    key={b.id}
                    justify="space-between"
                    align="center"
                    gap={2}
                    px={2}
                    py={1}
                    borderRadius="md"
                    bg={colors.GRAY_LIGHTEST}
                  >
                    <Box minW={0}>
                      <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                        {b.nomePagador || "sem nome"}
                      </Text>
                      <Text fontSize="2xs" color={colors.GRAY_DARK}>
                        doc {b.numeroDocumento || "—"}
                        {b.idLote ? ` · parcela ${b.parcela}` : ""}
                      </Text>
                    </Box>
                    <Box textAlign="right" flexShrink={0}>
                      <Text fontSize="xs" fontWeight="bold">
                        {fmtBRL(b.valor)}
                      </Text>
                      {porRecebimento && b.valorLiquido != null && (
                        <Text fontSize="2xs" color={colors.GRAY_DARK}>
                          líquido {fmtBRL(b.valorLiquido)}
                        </Text>
                      )}
                    </Box>
                  </Flex>
                ))}
              </VStack>
            )}

            {boletosDia.length >= 50 && (
              <Text fontSize="2xs" color={colors.GRAY_DARK} mt={2}>
                mostrando os 50 primeiros — veja todos no painel de gestão
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
